import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Trash2, ArrowLeft, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditInventory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [formData, setFormData] = useState({
    customer_name: '', company_name: '', customer_address: '',
    person_in_charge: '', customer_phone: '', customer_email: '',
    inventory_date: '', notes: ''
  });

  const [items, setItems] = useState([]);

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/inventory/${id}`);
        const r = res.data;
        setFormData({
          customer_name: r.customer_name || '', company_name: r.company_name || '',
          customer_address: r.customer_address || '', person_in_charge: r.person_in_charge || '',
          customer_phone: r.customer_phone || '', customer_email: r.customer_email || '',
          inventory_date: r.inventory_date ? r.inventory_date.split('T')[0] : '', notes: r.notes || ''
        });
        setItems((r.items || []).map(item => ({
          description: item.description || '', sku_reg_no: item.sku_reg_no || '',
          zone: item.zone || '', address: item.address || '', qty: item.qty || 0,
          record_qty: item.record_qty || 0, qty_in: item.qty_in || 0,
          qty_out: item.qty_out || 0, total_qty: item.total_qty || 0
        })));
      } catch (error) {
        toast.error('Failed to load record');
        navigate('/admin/inventory');
      } finally {
        setPageLoading(false);
      }
    };
    fetchRecord();
  }, [id, navigate]);

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    const recordQty = parseFloat(updated[index].record_qty) || 0;
    const qtyIn = parseFloat(updated[index].qty_in) || 0;
    const qtyOut = parseFloat(updated[index].qty_out) || 0;
    updated[index].total_qty = recordQty + qtyIn - qtyOut;
    setItems(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        items: items.map(item => ({
          ...item,
          qty: parseFloat(item.qty) || 0, record_qty: parseFloat(item.record_qty) || 0,
          qty_in: parseFloat(item.qty_in) || 0, qty_out: parseFloat(item.qty_out) || 0,
          total_qty: parseFloat(item.total_qty) || 0
        }))
      };
      await axios.put(`${BACKEND_URL}/api/inventory/${id}`, payload);
      toast.success('Inventory record updated');
      navigate(`/admin/inventory/${id}`);
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) return <div className="min-h-screen pt-44 px-4 flex justify-center"><p>Loading...</p></div>;

  return (
    <div className="min-h-screen pt-44 pb-12 px-4 bg-gray-50">
      <div className="container mx-auto max-w-5xl">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" onClick={() => navigate(`/admin/inventory/${id}`)}>
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <ClipboardList className="text-blue-600" /> Edit Stock Take
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Customer & Inventory Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Company Name</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} /></div>
                <div><Label>Customer Name *</Label><Input value={formData.customer_name} onChange={(e) => setFormData({...formData, customer_name: e.target.value})} required /></div>
              </div>
              <div><Label>Address</Label><Textarea value={formData.customer_address} onChange={(e) => setFormData({...formData, customer_address: e.target.value})} rows={2} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Person In Charge</Label><Input value={formData.person_in_charge} onChange={(e) => setFormData({...formData, person_in_charge: e.target.value})} /></div>
                <div><Label>Phone</Label><Input value={formData.customer_phone} onChange={(e) => setFormData({...formData, customer_phone: e.target.value})} /></div>
                <div><Label>Email</Label><Input type="email" value={formData.customer_email} onChange={(e) => setFormData({...formData, customer_email: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Inventory Date</Label><Input type="date" value={formData.inventory_date} onChange={(e) => setFormData({...formData, inventory_date: e.target.value})} /></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Stock Take Items</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, { description: '', sku_reg_no: '', zone: '', address: '', qty: 0, record_qty: 0, qty_in: 0, qty_out: 0, total_qty: 0 }])}>
                  <Plus size={14} className="mr-1" /> Add Row
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left">#</th>
                      <th className="border p-2 text-left">Description</th>
                      <th className="border p-2 text-left">SKU / Reg No</th>
                      <th className="border p-2 text-left">Zone</th>
                      <th className="border p-2 text-left">Address</th>
                      <th className="border p-2 text-center w-20">Qty</th>
                      <th className="border p-2 text-center w-20">Record Qty</th>
                      <th className="border p-2 text-center w-20">IN</th>
                      <th className="border p-2 text-center w-20">OUT</th>
                      <th className="border p-2 text-center w-20">Total Qty</th>
                      <th className="border p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td className="border p-1 text-center text-gray-500">{index + 1}</td>
                        <td className="border p-1"><Input className="h-8 text-sm" value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} /></td>
                        <td className="border p-1"><Input className="h-8 text-sm w-28" value={item.sku_reg_no} onChange={(e) => updateItem(index, 'sku_reg_no', e.target.value)} /></td>
                        <td className="border p-1"><Input className="h-8 text-sm w-20" value={item.zone} onChange={(e) => updateItem(index, 'zone', e.target.value)} /></td>
                        <td className="border p-1"><Input className="h-8 text-sm w-28" value={item.address} onChange={(e) => updateItem(index, 'address', e.target.value)} /></td>
                        <td className="border p-1"><Input type="number" className="h-8 text-sm w-16 text-center" value={item.qty} onChange={(e) => updateItem(index, 'qty', e.target.value)} /></td>
                        <td className="border p-1"><Input type="number" className="h-8 text-sm w-16 text-center" value={item.record_qty} onChange={(e) => updateItem(index, 'record_qty', e.target.value)} /></td>
                        <td className="border p-1"><Input type="number" className="h-8 text-sm w-16 text-center" value={item.qty_in} onChange={(e) => updateItem(index, 'qty_in', e.target.value)} /></td>
                        <td className="border p-1"><Input type="number" className="h-8 text-sm w-16 text-center" value={item.qty_out} onChange={(e) => updateItem(index, 'qty_out', e.target.value)} /></td>
                        <td className="border p-2 text-center font-semibold bg-gray-50">{item.total_qty}</td>
                        <td className="border p-1 text-center">
                          {items.length > 1 && <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setItems(items.filter((_, i) => i !== index))}><Trash2 size={14} /></Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
            <CardContent>
              <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={3} />
            </CardContent>
          </Card>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Updating...' : 'Update Stock Take Record'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default EditInventory;
